import { useMemo, useRef, useState } from 'react';

import { downloadBlob } from '@mint-lib/api-zod-support';
import { usePageOperation } from '@mint-lib/datadog';
import { useTranslation } from '@mint-lib/i18n';
import { enqueueSnackbar } from '@mint-lib/notifications';
import {
  type BaseFile,
  type FileUploaderV2RefType,
  type ReadyForUploadFile,
} from '@mint-lib/tw-components/patterns';
import type { MediaPlan } from '@mint-sdk/api-media-plan';

import {
  ImportAlreadyRunningError,
  useImportActivity,
} from '../../../../../../api/mutations/useImportActivity.js';
import { useImportTemplate } from '../../../../../../api/queries/useImportTemplate.js';
import { ImportPlanViews } from '../../../../../../types/import.types.js';

type UseImportPlanArgs = {
  handleOpenChange: (isOpen: boolean) => void;
  activity: MediaPlan.SidebarItem | undefined;
  isSelectionIncomplete: boolean;
};

export const useImportPlan = ({
  handleOpenChange,
  activity,
  isSelectionIncomplete,
}: UseImportPlanArgs) => {
  const { t } = useTranslation('@myn/planning_v2');

  const {
    data: errorLog,
    mutateAsync: importAsync,
    reset: resetImport,
    isIdle,
    isPending,
  } = useImportActivity();

  const {
    mutateAsync: downloadImportTemplateAsync,
    isPending: isDownloadingTemplate,
  } = useImportTemplate();

  // Started by "Import activity" in ImportPlanModal. Closing the modal while
  // it is open fails it as `abandoned`; a failure the user does not retry
  // stays `error`.
  const importOperation = usePageOperation('plan.import');

  const [files, setFiles] = useState<BaseFile[]>([]);

  const fileUploaderRef = useRef<FileUploaderV2RefType | null>(null);

  const isFileReadyForUpload = files.some((file) =>
    file.isReadyForUploadFile(),
  );

  const handleFileUpload = async (fileBlob: File) => {
    importOperation.restartIfEnded();

    await importAsync(
      {
        file: fileBlob,
        activityId: activity?.activityId || '',
      },
      {
        onSuccess: (errorLog) => {
          // A 207 carries the error log of a file that failed validation.
          if (errorLog) {
            importOperation.fail({ reason: 'validation' });
            return;
          }

          importOperation.succeed();
          enqueueSnackbar({
            title: t('Media plan imported successfully'),
            variant: 'success',
            removable: true,
          });
          handleOpenChange(false);
        },
        onError: (err) => {
          importOperation.fail({
            reason:
              err instanceof ImportAlreadyRunningError
                ? 'already-running'
                : 'request',
          });
          enqueueSnackbar({
            title: err.message,
            variant: 'error',
            removable: true,
          });

          handleOpenChange(false);
        },
      },
    );
  };

  const handleDownloadTemplateButton = async () => {
    if (!activity?.activityId) {
      return;
    }
    downloadImportTemplateAsync(activity.activityId);
  };

  const handleDownloadErrorLog = async () => {
    if (!errorLog) {
      return;
    }

    downloadBlob(errorLog.file, errorLog.fileName);
  };

  const handleUploadNewFileButton = () => {
    resetImport();
    setFiles([]);
  };

  const handleImport = () => {
    if (!fileUploaderRef.current) return;

    const readyForUploadFiles = files.filter(
      (file): file is ReadyForUploadFile => file.isReadyForUploadFile(),
    );

    fileUploaderRef.current.uploadFiles(readyForUploadFiles);
  };

  const isImportButtonDisabled =
    (isIdle && !isFileReadyForUpload) ||
    !!errorLog ||
    isPending ||
    isSelectionIncomplete;

  const isDownloadTemplateDisabled =
    isDownloadingTemplate || isSelectionIncomplete;

  const activeView = useMemo(() => {
    if (errorLog) {
      return ImportPlanViews.ERROR;
    }

    if (isPending) {
      return ImportPlanViews.PENDING;
    }

    return ImportPlanViews.UPLOAD;
  }, [errorLog, isPending]);

  return {
    activeView,
    fileUploaderRef,
    setFiles,
    handleFileUpload,
    isPending,
    isImportButtonDisabled,
    isDownloadTemplateDisabled,
    handleDownloadTemplateButton,
    handleUploadNewFileButton,
    handleDownloadErrorLog,
    handleImport,
  };
};
